import type { ModulesAndAgendasState } from '../index.ts';
import { isFormItemTabNameUnique } from './addNewSection.ts';
import {
  createEmptyFormItemTab,
  findSectionIndex,
  findTabIndex,
  getMaxGridSize,
  reindexFormItemTabs,
  syncFormItemTabsToGrid,
} from 'utils/section/tabSection.ts';
import { recalcSectionItems } from 'utils/section/items.ts';
import type { LocalSection } from 'types/agendaForm.ts';

const reindexSectionFormItemTabs = (section: LocalSection) => {
  if (section.formItemTabs.length === 0) {
    section.formItemTabs.push(createEmptyFormItemTab(null, { gridSizeX: section.gridSizeX, gridSizeY: section.gridSizeY }));
  }
  section.formItemTabs = reindexFormItemTabs(section.formItemTabs);
  section.items = recalcSectionItems(section.formItemTabs);
};

export interface Payload {
  value: { name?: string | null; newName: string; description?: string | null; number?: number | null };
  remove?: boolean;
  sectionName: string | null;
  currentTab: number;
  currentFormItemTab: number;
  parentSectionName?: string | null;
}

export const editFormItemTab = (state: ModulesAndAgendasState, { payload }: { payload: Payload }) => {
  const { value, currentTab, sectionName, currentFormItemTab, remove, parentSectionName } = payload;
  const tabSections = state.tabSections;

  const currentTabIndex = findTabIndex(tabSections, currentTab);

  if (currentTabIndex === -1) throw new Error('Tab index not found');

  const sectionIndex = findSectionIndex(tabSections[currentTabIndex].sections, sectionName);
  if (sectionIndex === -1) throw new Error('Section index not found');

  const section = tabSections[currentTabIndex].sections[sectionIndex];

  const formItemTabs = section.formItemTabs;

  const formItemTabIndex = findTabIndex(section.formItemTabs, currentFormItemTab);

  if (formItemTabIndex === -1) throw new Error('Form item tab index not found');

  if (remove) {
    section.formItemTabs.splice(formItemTabIndex, 1);
    reindexSectionFormItemTabs(section);
    return;
  }

  const isMovingToOtherParentSection = Boolean(parentSectionName && parentSectionName !== sectionName);

  if (isMovingToOtherParentSection) {
    const targetSectionIndex = findSectionIndex(tabSections[currentTabIndex].sections, parentSectionName ?? null);
    if (targetSectionIndex === -1) throw new Error('Section index not found');

    const targetSection = tabSections[currentTabIndex].sections[targetSectionIndex];

    if (!isFormItemTabNameUnique(targetSection.formItemTabs, value.newName ?? '')) {
      throw new Error('exists');
    }

    const [movedTab] = section.formItemTabs.splice(formItemTabIndex, 1);
    const maxGrid = getMaxGridSize(section, targetSection);

    targetSection.formItemTabs.push({
      ...movedTab,
      tab: {
        ...movedTab.tab,
        name: value.newName,
        description: value.description,
      },
    });

    targetSection.gridSizeX = maxGrid.gridSizeX;
    targetSection.gridSizeY = maxGrid.gridSizeY;
    targetSection.formItemTabs = syncFormItemTabsToGrid(targetSection.formItemTabs, maxGrid.gridSizeX, maxGrid.gridSizeY);

    reindexSectionFormItemTabs(section);
    reindexSectionFormItemTabs(targetSection);
    return;
  }

  if (value.newName !== value.name && !isFormItemTabNameUnique(formItemTabs, value.newName ?? '')) {
    throw new Error('exists');
  }

  const tab = formItemTabs[formItemTabIndex].tab;

  state.tabSections[currentTabIndex].sections[sectionIndex].formItemTabs[formItemTabIndex].tab = {
    ...tab,
    name: value.newName,
    description: value.description,
  };
};
